import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ImprovementPlanActionForm from "../ImprovementPlanActionForm";
import { getImprovementPlanById, getSelectorsData, updateImprovementPlan } from "../../api/improvementPlanAPI";
import { getImprovementPlanActionFormSchema } from "../../schema/improvementPlanActionSchema";
import { ImprovementPlanActionFormDto, ImprovementPlanFormDto } from "../../types";
import { resetForm, setForm } from "../../../../redux/improvementPlanSlice";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";

type GanttActionEditModalProps = {
    show: boolean;
    planId: number | null;
    actionId: number | null;
    onClose: () => void;
};

export default function GanttActionEditModal({ show, planId, actionId, onClose }: GanttActionEditModalProps) {
    const { t } = useTranslation(["improvementPlan", "common"]);
    const dispatch = useDispatch();
    const queryClient = useQueryClient();

    const [actionForm, setActionForm] = useState<ImprovementPlanActionFormDto | null>(null);
    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const planQuery = useQuery({
        queryKey: ["improvement-plan", planId],
        queryFn: () => getImprovementPlanById(planId as number),
        enabled: show && planId != null,
    });

    const selectorsQuery = useQuery({
        queryKey: ["improvement-plan-selectors"],
        queryFn: getSelectorsData,
        enabled: show,
    });

    const plan = planQuery.data ?? null;

    const editingIndex = useMemo(
        () => (plan && actionId != null ? plan.actions.findIndex((a) => a.id === actionId) : -1),
        [plan, actionId]
    );

    useEffect(() => {
        if (!plan || editingIndex < 0) return;
        dispatch(setForm(plan));
        setActionForm({ ...plan.actions[editingIndex] });
        setErrors({});
        setSaveError(null);
    }, [plan, editingIndex, dispatch]);

    const handleClose = () => {
        dispatch(resetForm());
        setActionForm(null);
        setErrors({});
        setSaveError(null);
        onClose();
    };

    const updateField = <FormField extends keyof ImprovementPlanActionFormDto>(
        field: FormField,
        value: ImprovementPlanActionFormDto[FormField]
    ) => {
        setActionForm((prev) => (prev ? { ...prev, [field]: value } : prev));
        setErrors((prev) => {
            const next = { ...prev, [field]: undefined };
            if (field === "comments") {
                Object.keys(next).forEach((key) => {
                    if (key.startsWith("comments[")) {
                        next[key] = undefined;
                    }
                });
            }
            return next;
        });
    };

    const handleSave = async () => {
        if (!plan || !actionForm || editingIndex < 0 || planId == null) return;

        const existingTitles = plan.actions
            .filter((_, index) => index !== editingIndex)
            .map((action) => action.description);

        const schema = getImprovementPlanActionFormSchema(t, existingTitles);
        setErrors({});
        setSaveError(null);

        try {
            await schema.validate(actionForm, { abortEarly: false });
        } catch (err: any) {
            const validationErrors: { [key: string]: string | undefined } = {};
            if (err?.inner && Array.isArray(err.inner) && err.inner.length > 0) {
                err.inner.forEach((error: any) => {
                    if (error.path) {
                        validationErrors[error.path] = error.message;
                    }
                });
            } else if (err?.path) {
                validationErrors[err.path] = err.message;
            }
            setErrors(validationErrors);
            return;
        }

        const updatedActions = plan.actions.map((action, index) => (index === editingIndex ? actionForm : action));
        const updatedPlan: ImprovementPlanFormDto = { ...plan, actions: updatedActions };

        setSaving(true);
        try {
            await updateImprovementPlan(planId, updatedPlan);
            await queryClient.invalidateQueries({ queryKey: ["improvement-plans-gantt"], exact: false, refetchType: "all" });
            queryClient.invalidateQueries({ queryKey: ["improvement-plan", planId] });
            handleClose();
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            setSaveError(fetchError?.message || t("common:error"));
        } finally {
            setSaving(false);
        }
    };

    const loading = planQuery.isLoading || selectorsQuery.isLoading;
    const loadError = planQuery.isError || selectorsQuery.isError;

    return (
        <Modal show={show} onHide={handleClose} size="xl" centered>
            <Modal.Header closeButton></Modal.Header>
            <Modal.Body>
                {saveError && <Alert variant="danger" onClose={() => setSaveError(null)} dismissible>{saveError}</Alert>}
                {loadError && <Alert variant="danger">{t("common:error")}</Alert>}
                {loading || !actionForm ? (
                    !loadError && <div className="text-center py-4"><Spinner animation="border" /></div>
                ) : (
                    <ImprovementPlanActionForm
                        actionForm={actionForm}
                        errors={errors}
                        updateField={updateField}
                        responsibleOptions={selectorsQuery.data?.responsibleOptions ?? []}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose} disabled={saving}>
                    {t("common:buttons.buttonCancel")}
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={saving || loading || !actionForm}>
                    {t("common:save")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
